import { formatCurrency, pluralize } from "@gigpro/utils";
import { parkingExpenseTypeLabels, TIP_POLICY_LABELS, type TipPolicy } from "api/resources/shared";
import {
  AUDIENCE_WORDS,
  audienceOf as postedAudience,
  dayLabel,
  type PostDraft,
  type PostPosition,
  type PostShift,
  type RowKey,
  timeRange,
} from "./model";

/** One value an edit changed, in the manager's words, keyed as `hand_set` keys its field ("day", "count:<role>"). */
export type Change = {
  card: string | null;
  key: string;
  words: string;
  /** The card row that shows it, with the role's key for a role's value; null for what no row shows (a PO number, a card). */
  row: RowKey | null;
  role?: string;
  /** Whose value it is, as a mark names it: the role's name, or the value's own ("the time"). */
  name: string;
  /** What changed, as Review lists it ("count", "pay"). */
  what: string;
  /** What it held before, in words; null when nothing was set. */
  was: string | null;
};

const LEADING_THE = /^the /;

/** A `hand_set` key's field: "count" of "count:<role>". */
export const fieldOf = (key: string) => {
  const at = key.indexOf(":");
  return at === -1 ? key : key.slice(0, at);
};

// Bookkeeping, not values: an edit that changes only these changed nothing.
const NOT_VALUES = new Set(["touched", "hand_set", "defaults", "saved_at", "marks"]);

/** Absent, null, "" and [] all say "not set". */
const unset = (value: unknown) =>
  value === undefined || value === "" || (Array.isArray(value) && value.length === 0) ? null : value;

/** Whether two drafts (or any parts of them) hold the same values, by value, never by object. */
export const sameValues = (a: unknown, b: unknown): boolean => {
  const [x, y] = [unset(a), unset(b)];
  if (x === y) return true;
  if (Array.isArray(x) && Array.isArray(y))
    return x.length === y.length && x.every((each, at) => sameValues(each, y[at]));
  if (!x || !y || typeof x !== "object" || typeof y !== "object" || Array.isArray(x) || Array.isArray(y)) return false;
  const left = x as Record<string, unknown>;
  const right = y as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => NOT_VALUES.has(key) || sameValues(left[key], right[key]));
};

const roleName = (role: PostPosition) => role.position_label || "the role";
const wage = (pay: number) => `${formatCurrency(pay)}/hr`;
const pros = (count: number) => `${count} ${pluralize(count, "pro")}`;
const audienceFields = (role: PostPosition) => [
  role.audience_type ?? "MARKETPLACE",
  role.selected_pro_ids,
  role.backup_to_marketplace,
];
const quoted = (text: string | null | undefined) => {
  const said = text?.trim();
  if (!said) return null;
  const short = said.length > 40 ? `${said.slice(0, 39)}…` : said;
  return `"${short}"`;
};
const TIP_POLICY_CHANGE: Record<TipPolicy | "CLEARED", (name: string) => string> = {
  INCLUDED: (name) => `${name} pay includes tips`,
  NOT_INCLUDED: (name) => `${name} pay doesn't include tips`,
  POOLED: (name) => `${name} pay includes pooled tips`,
  CLEARED: (name) => `Cleared ${name} tips`,
};
const tipWords = (tipPolicy: TipPolicy) => TIP_POLICY_LABELS[tipPolicy].toLowerCase();

/** A value's words, or null when it is not set. */
const shown = <Value>(value: Value | null | undefined, words: (value: Value) => string) =>
  unset(value) === null ? null : words(value as Value);

type At = (field: string, what: string, words: string, before: string | null) => Change;

/** A role's details beside its count, pay and audience: description, notes, requirements, uniform and the like. */
const roleDetails = (at: At, was: PostPosition, now: PostPosition, name: string): Array<Change> => {
  const changes: Array<Change> = [];
  if (!sameValues(was.description, now.description)) {
    changes.push(at("description", "description", `Changed ${name}'s description`, quoted(was.description)));
  }
  if (!sameValues(was.notes, now.notes))
    changes.push(at("notes", "notes", `Changed ${name}'s notes`, quoted(was.notes)));
  if (!sameValues(was.requirement_ids, now.requirement_ids)) {
    const before = shown(was.requirement_ids, (ids) => `${ids.length} ${pluralize(ids.length, "requirement")}`);
    changes.push(at("requirements", "requirements", `Changed ${name}'s requirements`, before));
  }
  if (!sameValues(was.uniform, now.uniform)) {
    changes.push(
      at(
        "uniform",
        "uniform",
        `Changed ${name}'s uniform`,
        shown(was.uniform, () => "another uniform"),
      ),
    );
  }
  if (!sameValues(was.attachment_ids, now.attachment_ids)) {
    const before = shown(was.attachment_ids, (ids) => `${ids.length} attached`);
    changes.push(at("role_attachments", "attachments", `Changed ${name}'s attachments`, before));
  }
  if (!sameValues(was.auto_select_enabled, now.auto_select_enabled)) {
    const words = `Turned auto-select ${now.auto_select_enabled ? "on" : "off"} for ${name}`;
    changes.push(
      at(
        "auto_select",
        "auto-select",
        words,
        shown(was.auto_select_enabled, (on) => (on ? "on" : "off")),
      ),
    );
  }
  return changes;
};

const roleChanges = (
  card: string,
  was: PostPosition | undefined,
  now: PostPosition,
  engageOn: boolean,
): Array<Change> => {
  const name = roleName(now);
  const at: At = (field, what, words, before) => ({
    card,
    key: `${field}:${now.key}`,
    words,
    row: "positions",
    role: now.key,
    name,
    what,
    was: before,
  });
  if (!was) {
    const pay = now.pay_rate == null ? "" : ` at ${wage(now.pay_rate)}`;
    return [at("position", "position", `Added ${name} × ${now.count}${pay}`, null)];
  }
  const wasName = roleName(was);
  const changes: Array<Change> = [];
  if (!sameValues(was.position_id, now.position_id)) {
    changes.push(at("position", "position", `Changed ${wasName} to ${name}`, wasName));
  } else if (!sameValues(was.position_label, now.position_label)) {
    changes.push(at("label", "position", `Changed ${wasName} to ${name}`, wasName));
  }
  if (was.count !== now.count) {
    changes.push(at("count", "count", `Set ${name} to ${pros(now.count)}`, pros(was.count)));
  }
  if (!sameValues(was.pay_rate, now.pay_rate)) {
    const words = now.pay_rate == null ? `Cleared ${name} pay` : `Set ${name} pay to ${wage(now.pay_rate)}`;
    changes.push(at("pay", "pay", words, shown(was.pay_rate, wage)));
  }
  if (!sameValues(was.tip_policy, now.tip_policy)) {
    changes.push(
      at("tips", "tips", TIP_POLICY_CHANGE[now.tip_policy ?? "CLEARED"](name), shown(was.tip_policy, tipWords)),
    );
  }
  if (!sameValues(audienceFields(was), audienceFields(now))) {
    const audience =
      was.audience_type === "PRIVATE_DIRECT_OFFER"
        ? "Preferred Workers"
        : AUDIENCE_WORDS[postedAudience(was, engageOn)];
    changes.push(at("audience", "who it goes to", `Changed who ${name} goes to`, audience));
  }
  return [...changes, ...roleDetails(at, was, now, name)];
};

type CardAt = (key: string, row: RowKey, name: string, words: string, before: string | null) => Change;

/** Parking, arrival, the manager and attachments: the card's rows below its day, time and address. */
const placeChanges = (at: CardAt, was: PostShift, now: PostShift): Array<Change> => {
  const changes: Array<Change> = [];
  if (!sameValues(was.parking_expense_type, now.parking_expense_type)) {
    const label = (type: NonNullable<PostShift["parking_expense_type"]>) =>
      parkingExpenseTypeLabels[type].toLowerCase();
    const words = now.parking_expense_type ? `Set parking to ${label(now.parking_expense_type)}` : "Cleared parking";
    changes.push(at("parking", "where", "parking", words, shown(was.parking_expense_type, label)));
  }
  if (!sameValues(was.parking_instructions, now.parking_instructions)) {
    const words = now.parking_instructions ? "Set the arrival instructions" : "Cleared the arrival instructions";
    changes.push(at("arrival", "where", "the arrival instructions", words, quoted(was.parking_instructions)));
  }
  if (!sameValues(was.manager?.id, now.manager?.id)) {
    const words = now.manager ? `Set the manager on duty to ${now.manager.fullname}` : "Cleared the manager on duty";
    changes.push(at("manager", "manager", "the manager", words, was.manager?.fullname ?? null));
  }
  if (!sameValues(was.attachment_ids, now.attachment_ids)) {
    const before = shown(was.attachment_ids, (ids) => `${ids.length} attached`);
    changes.push(at("attachments", "attachments", "the attachments", "Changed the attachments", before));
  }
  return changes;
};

/** What an edit changed on one card, value by value. */
export const cardChanges = (was: PostShift, now: PostShift, engageOn: boolean): Array<Change> => {
  const at: CardAt = (key, row, name, words, before) => ({
    card: now.key,
    key,
    words,
    row,
    name,
    what: name.replace(LEADING_THE, ""),
    was: before,
  });
  const changes: Array<Change> = [];
  if (!sameValues(was.date, now.date)) {
    const words = now.date ? `Set the day to ${dayLabel(now.date)}` : "Cleared the day";
    changes.push(at("day", "days", "the day", words, shown(was.date, dayLabel)));
  }
  if (!sameValues([was.start_time, was.end_time], [now.start_time, now.end_time])) {
    const words =
      now.start_time && now.end_time
        ? `Set the time to ${timeRange(now.start_time, now.end_time)}`
        : "Cleared the time";
    const before = was.start_time && was.end_time ? timeRange(was.start_time, was.end_time) : null;
    changes.push(at("time", "time", "the time", words, before));
  }
  if (!sameValues(was.unpaid_break_minutes, now.unpaid_break_minutes)) {
    const words = `Set the unpaid break to ${now.unpaid_break_minutes ?? 0} min`;
    changes.push(
      at(
        "time",
        "time",
        "the break",
        words,
        shown(was.unpaid_break_minutes, (min) => `${min} min`),
      ),
    );
  }
  if (!sameValues(was.address_id, now.address_id)) {
    changes.push(
      at(
        "where",
        "where",
        "the address",
        "Changed the address",
        shown(was.address_id, () => "another address"),
      ),
    );
  }
  if (!sameValues(was.parking_attachment_ids, now.parking_attachment_ids)) {
    const before = shown(was.parking_attachment_ids, (ids) => `${ids.length} attached`);
    changes.push(at("where", "where", "the parking photos", "Changed the parking photos", before));
  }
  changes.push(...placeChanges(at, was, now));
  for (const role of now.positions) {
    changes.push(
      ...roleChanges(
        now.key,
        was.positions.find(({ key }) => key === role.key),
        role,
        engageOn,
      ),
    );
  }
  for (const role of was.positions.filter(({ key }) => !now.positions.some((each) => each.key === key))) {
    const name = roleName(role);
    changes.push({
      card: now.key,
      key: `removed:${role.key}`,
      words: `Removed ${name}`,
      row: null,
      name,
      what: name,
      was: null,
    });
  }
  return changes;
};

/** What an edit changed on the whole draft: its cards, and the PO number on the draft itself. */
const draftChanges = (before: PostDraft, after: PostDraft, engageOn: boolean): Array<Change> => {
  const whole = (card: string | null, key: string, words: string): Change => ({
    card,
    key,
    words,
    row: null,
    name: key,
    what: key,
    was: null,
  });
  const changes: Array<Change> = [];
  if (!sameValues(before.po_number, after.po_number)) {
    const po = after.po_number;
    changes.push(whole(null, "po_number", po ? `Set the PO number to ${po}` : "Cleared the PO number"));
  }
  for (const shift of after.shifts) {
    const was = before.shifts.find(({ key }) => key === shift.key);
    changes.push(...(was ? cardChanges(was, shift, engageOn) : [whole(shift.key, "card", "Added a card")]));
  }
  for (const shift of before.shifts.filter(({ key }) => !after.shifts.some((each) => each.key === key))) {
    changes.push(whole(shift.key, "card", "Removed a card"));
  }
  return changes;
};

/**
 * `said` in a page action's result: the manager's words for each value the edit changed, one per line, or "" when it
 * changed none. Whether anything changed is told from the whole draft, so a value without words of its own still counts.
 */
export const saidOf = (before: PostDraft, after: PostDraft, engageOn: boolean) =>
  sameValues(before, after)
    ? ""
    : draftChanges(before, after, engageOn)
        .map(({ words }) => words)
        .join("\n") || "Changed the card";
